import { type ReactNode, useMemo, useState } from 'react';

import type { RouteRef, SubRouteRef } from '@backstage/core-plugin-api';

import { Page } from '@spotify/confidence-common-react';
import {
  FilterBar,
  FiltersProvider,
  type ViewMode,
  ViewModeToggle,
} from '@spotify/confidence-core-react';
import {
  FAVORITES_FILTER,
  useFavoritesFilter,
} from '@spotify/plugin-confidence-collections-react';

import { type WorkflowExtensionOptions } from '../../extensions';
import { useWorkflowExtensions } from '../../hooks';
import { CreateExperimentMenu } from './CreateExperimentMenu';

type FiltersProviderProps = Parameters<typeof FiltersProvider>[0];

export type ExperimentsPageLayoutProps = {
  routeRef: RouteRef | SubRouteRef;
  storageKey: string;
  filters: FiltersProviderProps['filters'];
  quickFilterIds?: FiltersProviderProps['quickFilterIds'];
  defaultFilters?: FiltersProviderProps['defaultFilters'];
  filterExtensions?: (
    extensions: WorkflowExtensionOptions[],
  ) => WorkflowExtensionOptions[];
  renderTable: (props: { mode: ViewMode }) => ReactNode;
};

export const ExperimentsPageLayout = ({
  routeRef,
  storageKey,
  filters,
  quickFilterIds,
  defaultFilters,
  filterExtensions,
  renderTable,
}: ExperimentsPageLayoutProps) => {
  const [mode, setMode] = useState<ViewMode>('Table');
  const extensions = useWorkflowExtensions();

  const visibleExtensions = useMemo(
    () => (filterExtensions ? filterExtensions(extensions) : extensions),
    [extensions, filterExtensions],
  );

  const creatableExtensions = useMemo(
    () => visibleExtensions.filter(ext => ext.createDialog),
    [visibleExtensions],
  );

  const favoritesFilter = useFavoritesFilter();

  return (
    <Page.Root routeRef={routeRef}>
      <Page.Wrapper
        component={FiltersProvider}
        storageKey={storageKey}
        searchField="displayName"
        filters={[favoritesFilter, ...(filters ?? [])]}
        quickFilterIds={[FAVORITES_FILTER.id, ...(quickFilterIds ?? [])]}
        defaultFilters={defaultFilters}
      />
      <Page.Content scrollButton>
        <Page.ContentActionBar>
          <ViewModeToggle mode={mode} onModeChange={setMode}  data-track-source-anchor="47038e33da;experiments-page-layout-view-mode-toggle"/>
          <FilterBar />
          {creatableExtensions.length > 0 && (
            <CreateExperimentMenu extensions={creatableExtensions} />
          )}
        </Page.ContentActionBar>
        <Page.ContentMain>{renderTable({ mode })}</Page.ContentMain>
      </Page.Content>
    </Page.Root>
  );
};
